import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { formatAmount } from "@/lib/api";

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const Field = ({ label, value, onChange, testid, suffix }) => (
  <label className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <div className="panel flex items-center gap-2 px-3 py-2" style={{ background: "#0F0F11" }}>
      <input
        type="number"
        min="0"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 min-w-0 bg-transparent outline-none text-right font-mono-num text-sm"
        data-testid={testid}
      />
      {suffix && <span className="text-[10px] text-zinc-500 font-mono-num">{suffix}</span>}
    </div>
  </label>
);

const Rule = ({ label, rule, setRule, prefix }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
    <div className="flex gap-2">
      {["reset", "increase"].map((m) => (
        <button
          key={m}
          onClick={() => setRule({ ...rule, mode: m })}
          className={`flex-1 py-2 rounded-lg text-[10px] uppercase tracking-wider ${rule.mode === m ? "btn-cyan" : "btn-ghost"}`}
          data-testid={`${prefix}-${m}`}
        >
          {m === "reset" ? "Reset" : "Increase"}
        </button>
      ))}
      <div className="panel flex items-center px-2 w-20" style={{ background: "#0F0F11", opacity: rule.mode === "increase" ? 1 : 0.4 }}>
        <input
          type="number"
          min="0"
          value={rule.pct}
          disabled={rule.mode !== "increase"}
          onChange={(e) => setRule({ ...rule, pct: e.target.value })}
          className="w-full bg-transparent outline-none text-right font-mono-num text-sm"
          data-testid={`${prefix}-pct`}
        />
        <span className="text-[10px] text-zinc-500">%</span>
      </div>
    </div>
  </div>
);

export default function AutobetPanel({ amount, setAmount, placeBet, currency, disabled }) {
  const [numBets, setNumBets] = useState(10);
  const [onWin, setOnWin] = useState({ mode: "reset", pct: 0 });
  const [onLoss, setOnLoss] = useState({ mode: "reset", pct: 0 });
  const [stopProfit, setStopProfit] = useState(0);
  const [stopLoss, setStopLoss] = useState(0);
  const [running, setRunning] = useState(false);
  const [stats, setStats] = useState({ bets: 0, wins: 0, profit: 0 });
  const runRef = useRef(false);

  useEffect(() => () => { runRef.current = false; }, []);

  const next = (rule, cur, base) => (rule.mode === "reset" ? base : cur * (1 + Number(rule.pct || 0) / 100));

  const start = async () => {
    const base = Number(amount);
    if (base <= 0) return toast.error("Enter a bet");
    runRef.current = true;
    setRunning(true);
    let cur = base, profit = 0, bets = 0, wins = 0;
    setStats({ bets, wins, profit });
    while (runRef.current && (Number(numBets) === 0 || bets < Number(numBets))) {
      const bet = await placeBet(Number(cur.toFixed(8)));
      if (!bet) break;
      bets += 1;
      profit += bet.profit;
      if (bet.profit > 0) wins += 1;
      setStats({ bets, wins, profit });
      if (Number(stopProfit) > 0 && profit >= Number(stopProfit)) { toast.success("Stop-profit reached"); break; }
      if (Number(stopLoss) > 0 && -profit >= Number(stopLoss)) { toast.error("Stop-loss reached"); break; }
      cur = next(bet.profit > 0 ? onWin : onLoss, cur, base);
      setAmount(Number(cur.toFixed(8)));
      await sleep(400);
    }
    runRef.current = false;
    setRunning(false);
  };

  return (
    <div className="flex flex-col gap-3" data-testid="autobet-panel">
      <div className="grid grid-cols-1 gap-3">
        <Field label="Number of bets (0 = ∞)" value={numBets} onChange={setNumBets} testid="autobet-count" />
        <Rule label="On win" rule={onWin} setRule={setOnWin} prefix="autobet-win" />
        <Rule label="On loss" rule={onLoss} setRule={setOnLoss} prefix="autobet-loss" />
        <div className="grid grid-cols-2 gap-2">
          <Field label="Stop on profit" value={stopProfit} onChange={setStopProfit} testid="autobet-stop-profit" suffix={currency} />
          <Field label="Stop on loss" value={stopLoss} onChange={setStopLoss} testid="autobet-stop-loss" suffix={currency} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center" data-testid="autobet-stats">
        <div className="panel py-2"><div className="text-[9px] uppercase text-zinc-500">Bets</div><div className="font-mono-num text-sm" data-testid="autobet-stat-bets">{stats.bets}</div></div>
        <div className="panel py-2"><div className="text-[9px] uppercase text-zinc-500">Wins</div><div className="font-mono-num text-sm text-emerald-400" data-testid="autobet-stat-wins">{stats.wins}</div></div>
        <div className="panel py-2"><div className="text-[9px] uppercase text-zinc-500">Profit</div><div className={`font-mono-num text-sm ${stats.profit >= 0 ? "text-emerald-400" : "text-rose-400"}`} data-testid="autobet-stat-profit">{formatAmount(stats.profit, currency)}</div></div>
      </div>
      {running ? (
        <button onClick={() => { runRef.current = false; }} className="btn-ghost rounded-xl py-4 font-display font-bold text-base tracking-wide text-rose-400" data-testid="autobet-stop">
          Stop Autobet
        </button>
      ) : (
        <button onClick={start} disabled={disabled} className="btn-gold rounded-xl py-4 font-display font-bold text-base tracking-wide" data-testid="autobet-start">
          Start Autobet
        </button>
      )}
    </div>
  );
}
